import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Checkbox from '@material-ui/core/Checkbox';
import MaterialTable from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import TableHead from './TableHead.js';
import Pagination from './TablePagination.js';
import TableExpandableRow from './TableExpandableRow.js';

const styles = (theme) => ({
  root: {
    width: '100%',
    marginTop: theme.spacing(3),
    padding: '0 15px',
    background: theme.palette.xpl.foreground,
    boxShadow: 'none',
  },
  noBackground: {
    background: 'transparent',
    boxShadow: 'none',
    padding: 0,
    margin: 0,
  },
  border: {
    border: `1px solid ${theme.palette.xpl.stoke}`,
  },
  table: {
    '& tr:last-child td': {
      borderBottom: 'none',
    },
    '& .MuiCheckbox-colorSecondary.Mui-checked': {
      color: theme.palette.xpl.secondary.default,
    },
    '& .MuiTableRow-root.Mui-selected': {
      background: theme.palette.xpl.foreground,
    },
    '& .MuiTableRow-root.Mui-selected:hover': {
      background: theme.palette.xpl.secondary.background,
    },
    '& .MuiCheckbox-root': {
      padding: '0px',
    },
  },
  tableWrapper: {
    // overflowX: 'auto',
  },
  cell: {
    padding: '18px 15px',
    color: theme.palette.xpl.text.secondary,
    lineHeight: '20px',
    borderBottomColor: theme.palette.xpl.stoke,
  },
  cellMobile: {
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  row: {},
  noResults: {
    textAlign: 'center',
    fontSize: '22px',
    color: theme.palette.xpl.text.primary,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '12px',
    lineHeight: '16px',
    height: 60,
  },
  pagination: {
    marginLeft: 'auto',
  },
});

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function Table(props) {
  const {
    classes,
    data,
    metadata,
    showNoResults,
    footerText,
    enableSorting,
    showFooter,
    noBackground,
    showEmptyRows,
    currentPage,
    onPageChange,
    showBorder,
    enableExpandableRow,
    expandableColumn,
    checkboxSelection,
    selectedItems,
    setSelectedItems,
    onRowClick,
  } = props;

  const [orderBy, setOrderBy] = React.useState('id');
  const [order, setOrder] = React.useState('asc');
  const [rowsPerPage, setRowsPerPage] = React.useState(props.rowsPerPage || 10);
  React.useEffect(() => {
    setRowsPerPage(props.rowsPerPage || 10);
  }, [props.rowsPerPage]);

  const page = currentPage || 0;
  const emptyRows = showEmptyRows
    ? rowsPerPage - Math.min(rowsPerPage, data.length - page * rowsPerPage)
    : 0;

  const defaultColWidth = (1 / metadata.length) * 100 + '%';
  const pageCount = Math.ceil(data.length / rowsPerPage);
  const showHeader = !!metadata.find((row) => !!row.label);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    onPageChange({ selected: 0 });
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = data.map((n) => n.id);
      setSelectedItems(newSelecteds);
      return;
    }
    setSelectedItems([]);
  };

  const handleClick = (event, id) => {
    if (checkboxSelection) {
      const selectedIndex = selectedItems.indexOf(id);
      let newSelected = [];

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selectedItems, id);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selectedItems.slice(1));
      } else if (selectedIndex === selectedItems.length - 1) {
        newSelected = newSelected.concat(selectedItems.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selectedItems.slice(0, selectedIndex),
          selectedItems.slice(selectedIndex + 1)
        );
      }

      setSelectedItems(newSelected);
    } else {
      if (onRowClick) {
        onRowClick(event, id);
      }
    }
  };

  const isSelected = (id) => selectedItems.indexOf(id) !== -1;
  return (
    <div>
      <Paper
        className={classNames(classes.root, {
          [classes.noBackground]: noBackground,
          [classes.border]: showBorder,
        })}>
        <div className={classes.tableWrapper}>
          <MaterialTable className={classes.table} aria-labelledby="tableTitle">
            <colgroup>
              {checkboxSelection && (
                <col key="table-checkbox" width="24px"></col>
              )}
              {metadata.map((r) => {
                return <col key={r.id} width={r.width || defaultColWidth} />;
              })}
            </colgroup>
            {showHeader && (
              <TableHead
                numSelected={checkboxSelection ? selectedItems.length : 0}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={data.length}
                rows={metadata}
                enableSorting={enableSorting}
                checkboxSelection={checkboxSelection}
              />
            )}
            {showNoResults ? (
              <TableBody>
                <TableRow style={{ height: 49 }}>
                  <TableCell
                    colSpan={metadata.length}
                    className={classNames(classes.cell, classes.noResults)}>
                    No Results Found
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableBody>
                {stableSort(data, getComparator(order, orderBy))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, i) => {
                    const isItemSelected = checkboxSelection
                      ? isSelected(row.id)
                      : false;
                    const labelId = `table-checkbox-${i}`;
                    if (enableExpandableRow && row.__childrens?.length > 0) {
                      return (
                        <TableExpandableRow
                          metadata={metadata}
                          row={row}
                          expandableColumn={expandableColumn}
                          hover
                          onClick={(event) => handleClick(event, row.id)}
                          role="checkbox"
                          aria-checked={isItemSelected}
                          tabIndex={-1}
                          key={i}
                          selected={isItemSelected}
                          className={classes.row}
                        />
                      );
                    }
                    return (
                      <TableRow
                        hover
                        onClick={(event) => handleClick(event, row.id)}
                        role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        key={i}
                        selected={isItemSelected}
                        className={classes.row}>
                        {checkboxSelection && (
                          <TableCell
                            padding="checkbox"
                            className={classes.cell}>
                            <Checkbox
                              checked={isItemSelected}
                              inputProps={{ 'aria-labelledby': labelId }}
                            />
                          </TableCell>
                        )}
                        {metadata.map((column, j) => (
                          <TableCell
                            key={j}
                            align={column.alignment}
                            className={classNames(
                              classes.cell,
                              column.className,
                              { [classes.cellMobile]: column.hideInMobile }
                            )}>
                            {row[column.renderingField]}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 49 * emptyRows }}>
                    <TableCell
                      colSpan={metadata.length}
                      className={classes.cell}
                    />
                  </TableRow>
                )}
              </TableBody>
            )}
          </MaterialTable>
        </div>
      </Paper>

      {showFooter && (
        <div className={classes.footer}>
          <div>{footerText}</div>
          {pageCount > 1 && (
            <Pagination
              className={classes.pagination}
              dataCount={data.length}
              onPageChange={onPageChange}
              currentPage={page}
              rowsPerPage={rowsPerPage}
              pageCount={pageCount}
            />
          )}
        </div>
      )}
    </div>
  );
}

Table.propTypes = {
  classes: PropTypes.object.isRequired,
  data: PropTypes.array.isRequired,
  metadata: PropTypes.array.isRequired,
  onRowClick: PropTypes.func,
  showNoResults: PropTypes.bool,
  rowsPerPage: PropTypes.number,
  footerText: PropTypes.object,
  currentPage: PropTypes.number,
  onPageChange: PropTypes.func,
  enableSorting: PropTypes.bool,
  showFooter: PropTypes.bool,
  noBackground: PropTypes.bool,
  showEmptyRows: PropTypes.bool,
  showBorder: PropTypes.bool,
  enableExpandableRow: PropTypes.bool,
  expandableColumn: PropTypes.string,
  checkboxSelection: PropTypes.bool,
  selectedItems: PropTypes.array,
  setSelectedItems: PropTypes.func,
};

export default withStyles(styles)(Table);
